import i18n from 'i18next';
import {
  Alert,
  ButtonGroup,
  Dialog,
  Form,
  FormButton,
  FormField,
  useTranslationContext,
  type FormSubmitHandler,
} from 'platform/components';
import {Hide, Show, Spinner} from 'platform/foundation';
import {VStack} from 'platform/foundation-tw';

import {head} from 'ramda';

import {suffixTestId, type TestIdProps} from 'shared/web';

import type {AsyncExportDataRequestBody} from '../../../../../../types/api/data';
import {useExport} from '../../hooks/useExport';
import type {ExportOptions} from '../../types/ExportOptions';

type ExportFileType = AsyncExportDataRequestBody['fileType'];

type ExportOptionsLocal = Omit<ExportOptions, 'fileType'> & {
  fileType: ExportFileType[];
};

const FILE_TYPE_OPTIONS = [
  {value: 'excel', label: 'Excel'},
  {value: 'csv', label: 'CSV'},
  {value: 'pdf', label: 'PDF'},
];

const FORM_DEFAULT_VALUES: ExportOptionsLocal = {
  fileType: ['csv'],
  columnDelimiter: 'comma',
  decimalDelimiter: 'dot',
};

const getDecimalDelimiters = () => [
  {value: 'comma', label: i18n.t('page.dataGrid.export.decimalDelimiter.comma')},
  {value: 'dot', label: i18n.t('page.dataGrid.export.decimalDelimiter.dot')},
];

const getColumnDelimiters = () => [
  {value: 'comma', label: i18n.t('page.dataGrid.export.columnDelimiter.comma')},
  {value: 'semicolon', label: i18n.t('page.dataGrid.export.columnDelimiter.semicolon')},
];

export type ExportDialogProps = {
  isOpen: boolean;
  onClose: () => void;
} & TestIdProps;

export function ExportDialog(props: ExportDialogProps) {
  const t = useTranslationContext();
  const {exportState, onExport} = useExport({onClose: props.onClose});

  const onHandleSubmit: FormSubmitHandler<ExportOptionsLocal> = async (values) => {
    await onExport({
      ...values,
      fileType: head(values.fileType) ?? 'csv',
    });
  };

  return (
    <Dialog
      title={t('page.datagrid.export.optionsTitle')}
      isOpen={props.isOpen}
      onClose={props.onClose}
      size="small"
      data-testid={suffixTestId('exportOptionsDialog', props)}
    >
      <Form<ExportOptionsLocal> onSubmit={onHandleSubmit} defaultValues={FORM_DEFAULT_VALUES}>
        {(control, formApi) => {
          const fileType = head(formApi.watch('fileType'));

          return (
            <VStack spacing={4}>
              <FormField
                control={control}
                name="fileType"
                type="chips"
                options={FILE_TYPE_OPTIONS}
                label={t('page.datagrid.export.fileType')}
                data-testid={suffixTestId('exportOptionsDialog-fileType', props)}
              />
              <Hide when={fileType === 'pdf'}>
                <FormField
                  control={control}
                  name="decimalDelimiter"
                  isNotClearable
                  menuInPortal
                  type="choice"
                  options={getDecimalDelimiters()}
                  label={t('page.datagrid.export.decimalSeparator')}
                  data-testid={suffixTestId('exportOptionsDialog-decimalSeparator', props)}
                />
                <Show when={fileType === 'csv'}>
                  <FormField
                    control={control}
                    name="columnDelimiter"
                    isNotClearable
                    menuInPortal
                    type="choice"
                    options={getColumnDelimiters()}
                    label={t('page.datagrid.export.columnSeparator')}
                    data-testid={suffixTestId('exportOptionsDialog-columnSeparator', props)}
                  />
                </Show>
              </Hide>

              <Show when={fileType === 'pdf'}>
                <Alert variant="warning" message={t('page.datagrid.export.columnLengthAlert')} />
              </Show>

              {exportState === 'exporting' && <Spinner variant="overlay" />}

              {exportState !== 'exporting' && (
                <ButtonGroup align="right">
                  <FormButton
                    control={control}
                    type="submit"
                    title={t('page.datagrid.button.export')}
                    data-testid={suffixTestId('exportOptionsDialog-submitButton', props)}
                  />
                </ButtonGroup>
              )}
            </VStack>
          );
        }}
      </Form>
    </Dialog>
  );
}
